/**
 * useDisplayNameEditor Hook
 *
 * Manages device display name editing functionality:
 * - Edit mode state
 * - Input value management
 * - Save/cancel operations
 * - API integration for updating display names
 * - Optimistic UI updates with cache invalidation
 */

import { useState, useCallback, useRef } from "react";
import { useDevicesStore } from "../stores";
import logger from "../utils/logger";

export function useDisplayNameEditor({
  selectedDeviceId,
  deviceStates,
  updateDeviceDisplayName,
  invalidateDeviceStateCache,
  fetchDeviceState,
  showNotification,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editingValue, setEditingValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const { setDevices, updateDeviceState } = useDevicesStore();

  // Use ref to avoid stale closure issues
  const selectedDeviceIdRef = useRef(selectedDeviceId);
  selectedDeviceIdRef.current = selectedDeviceId;

  const startEditing = useCallback(() => {
    const currentDeviceState = deviceStates[selectedDeviceIdRef.current];
    if (currentDeviceState?.displayName) {
      setEditingValue(currentDeviceState.displayName);
      setIsEditing(true);
    } else {
      setEditingValue("");
      setIsEditing(true);
      logger.warn(
        "Attempting to edit display name, but it's not present in current state."
      );
    }
  }, [deviceStates]);

  const handleInputChange = useCallback((event) => {
    setEditingValue(event.target.value);
  }, []);

  const cancelEditing = useCallback(() => {
    setIsEditing(false);
    setEditingValue("");
    setIsSaving(false);
  }, []);

  const saveDisplayName = useCallback(async () => {
    const currentDeviceId = selectedDeviceIdRef.current;
    const currentDeviceState = deviceStates[currentDeviceId];
    const currentName = currentDeviceState?.displayName;
    const newName = editingValue.trim();

    if (isSaving) return;

    // Validation
    if (!currentDeviceId || newName === currentName) {
      if (newName === currentName) {
        cancelEditing();
        return;
      }
    }

    if (newName.length === 0 || newName.length > 50) {
      showNotification({
        message: "Display Name must be 1-50 characters.",
        type: "error",
      });
      return;
    }

    setIsSaving(true);
    try {
      await updateDeviceDisplayName(currentDeviceId, newName);

      // Invalidate cache immediately after mutation to prevent stale data
      invalidateDeviceStateCache(currentDeviceId);

      // Pessimistic UI Update: Only update state AFTER successful API call
      updateDeviceState(currentDeviceId, {
        displayName: newName,
      });

      setDevices((prevDevices) => {
        const updated = prevDevices.map((dev) =>
          dev.deviceId === currentDeviceId
            ? { ...dev, displayName: newName }
            : dev
        );
        return updated.sort((a, b) =>
          (a.displayName || a.serialNumber || a.deviceId).localeCompare(
            b.displayName || b.serialNumber || b.deviceId
          )
        );
      });

      showNotification({
        message: "Display name updated successfully!",
        type: "success",
      });
      cancelEditing();

      // Refresh the specific device state to get updated data from backend
      // Note: Backend Lambda now updates the cache immediately, so the next
      // page reload will have the correct display name without waiting for
      // the background sync.
      fetchDeviceState(currentDeviceId);
    } catch (error) {
      logger.error("Failed to save display name:", error);
      showNotification({
        message: `Save failed: ${error.message}`,
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    editingValue,
    deviceStates,
    showNotification,
    cancelEditing,
    isSaving,
    fetchDeviceState,
    invalidateDeviceStateCache,
    setDevices,
    updateDeviceDisplayName,
    updateDeviceState,
  ]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        saveDisplayName();
      }
    },
    [saveDisplayName]
  );

  return {
    isEditing,
    editingValue,
    isSaving,
    startEditing,
    handleInputChange,
    cancelEditing,
    saveDisplayName,
    handleKeyDown,
  };
}
